import React, { useState, useMemo, useCallback, useEffect } from "react";
import { API, Auth, Hub } from "aws-amplify";
import Modal from "react-bootstrap/Modal";
import { InlineSpinner } from "components/Spinner";

function isValidReminderName(reminderName) {
  const rg1 = /^[a-zA-Z0-9 ]+$/; // letters, numbers, spaces
  return rg1.test(reminderName);
}

export default function OpenQueryReminderModal(props) {
  const [errorMsg, setErrorMsg] = useState("");

  const [reminderName, setReminderName] = useState("");
  const [dateType, setDateType] = useState("");
  const [dateValue, setDateValue] = useState("");
  const [savingReminder, setSavingReminder] = useState(false);

  // Auth readiness gate
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) setReady(false);
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  const ensureAuthed = async () => {
    await Auth.currentAuthenticatedUser();
  };

  useEffect(() => {
    if (props.reminder) {
      const base = props.reminder.REMINDER_DESCRIPTION || "";
      const parts = base.split(" -- ");
      setReminderName(parts.length > 1 ? parts[0] : "");
      setDateType(props.reminder.DATE_TYPE || "");
      setDateValue(props.reminder.DATE_VALUE || "");
    } else {
      setReminderName("");
      setDateType("");
      setDateValue("");
    }
  }, [props.reminder]);

  const reminderDescription = useMemo(() => {
    if (props.reminder) {
      const base = props.reminder.REMINDER_DESCRIPTION || "";
      const tail = base.split(" -- ").slice(1).join(" -- ") || base;
      return `${reminderName} -- ${tail}`;
    }
    if (props.job) {
      const host = `account.perceptionhealth.${
        process.env.REACT_APP_TARGET === "dev" ? "dev" : "com"
      }`;
      const reportName = props.job.report.split(".").join(" / ");
      return `${reminderName} -- Click here to submit a new ${reportName}  report:  https://${host}/openquery/job/${props.job.id}`;
    }
    return "";
  }, [props.job, props.reminder, reminderName]);

  const handleCreateReminder = useCallback(() => {
    const createReminder = async () => {
      try {
        if (!ready) return;
        await ensureAuthed();

        setSavingReminder(true);
        await API.post("OpenQueryAPI", `/reminders`, {
          body: {
            DATE_TYPE: dateType,
            DATE_VALUE: dateValue,
            REMINDER_DESCRIPTION: reminderDescription,
          },
        });
        setSavingReminder(false);
        props.handleCloseReminderModal();
      } catch (err) {
        setSavingReminder(false);
        setErrorMsg("error creating new reminder");
        console.error("error creating new reminder", err);
      }
    };
    createReminder();
  }, [props, reminderDescription, dateType, dateValue, ready]);

  const handleEditReminder = useCallback(() => {
    if (!props.reminder) return;

    const editReminder = async () => {
      try {
        if (!ready) return;
        await ensureAuthed();

        setSavingReminder(true);
        await API.put("OpenQueryAPI", `/reminders/${props.reminder.REMINDER_ID}`, {
          body: {
            DATE_TYPE: dateType,
            DATE_VALUE: dateValue,
            REMINDER_DESCRIPTION: reminderDescription,
          },
        });

        // refresh rendered list (parity with old script)
        if (typeof props.setReminders === "function") {
          const reminders = await API.get("OpenQueryAPI", `/reminders`);
          props.setReminders(reminders.Items);
        }

        setSavingReminder(false);
        props.handleCloseReminderModal();
      } catch (err) {
        setSavingReminder(false);
        setErrorMsg("error saving reminder changes");
        console.error("error saving reminder changes", err);
      }
    };
    editReminder();
  }, [props, reminderDescription, dateType, dateValue, ready]);

  const saveDisabled =
    !ready || !reminderName || !dateType || !dateValue || !isValidReminderName(reminderName);

  return (
    <>
      <Modal show={props.showReminderModal} onHide={props.handleCloseReminderModal}>
        <Modal.Header closeButton>
          <Modal.Title>{props.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="container-fluid">
            {props.job && (
              <>
                <div className="row">
                  <h5>{props.job.report}</h5>
                  <p>{props.job.id}</p>
                </div>
                <div className="row">
                  <div className="col pl-0">
                    <h6>Parameters</h6>
                    <ul className="list-unstyled">
                      {/* ★ Guard against missing definition/parameters */}
                      {Object.keys(props.job.definition?.parameters || {}).map((key) => (
                        <li key={key}>
                          <strong>{key}</strong>:{" "}
                          <span>{props.job.definition?.parameters?.[key]}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="col pl-0">
                    <h6>Output Fields</h6>
                    <p>{props.job.definition?.outputFields?.join(", ") || ""}</p>
                  </div>
                </div>
              </>
            )}

            <div className="form-group row">
              {/* ★ Add htmlFor + id for accessibility */}
              <label htmlFor="reminderName" className="col-4 col-form-label">
                Reminder Name:
              </label>
              <input
                id="reminderName" // ★
                type="text"
                className={`form-control col-8 ${
                  reminderName && !isValidReminderName(reminderName) ? "is-invalid" : ""
                }`}
                placeholder="Input Reminder Name"
                value={reminderName}
                onChange={(e) => setReminderName(e.target.value)}
                aria-invalid={Boolean(reminderName && !isValidReminderName(reminderName))} // ★
              />
              {reminderName && !isValidReminderName(reminderName) && (
                <div className="invalid-feedback text-right">
                  Must be a valid name (only letters, numbers, spaces)
                </div>
              )}
            </div>

            <div className="form-group row">
              <label htmlFor="dateType" className="col-4 col-form-label">{/* ★ */}
                Date Type:
              </label>
              <select
                id="dateType" // ★
                className="form-control col-8"
                value={dateType}
                onChange={(e) => {
                  setDateType(e.target.value);
                  setDateValue("");
                }}
              >
                <option value="">Select Type</option>
                <option value="DAY OF WEEK">Day of Week</option>
                <option value="DAY OF MONTH">Day of Month</option>
              </select>
            </div>

            {dateType && (
              <div className="form-group row">
                <label htmlFor="dateValue" className="col-4 col-form-label">{/* ★ */}
                  Date Value:
                </label>
                <select
                  id="dateValue" // ★
                  className="form-control col-8"
                  value={dateValue}
                  onChange={(e) => setDateValue(e.target.value)}
                >
                  <option value="">Select Value</option>
                  {dateType === "DAY OF WEEK" &&
                    ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"].map(
                      (weekDay) => (
                        <option value={weekDay} key={weekDay}>
                          {weekDay.slice(0, 1).toUpperCase() + weekDay.slice(1).toLowerCase()}
                        </option>
                      )
                    )}
                  {dateType === "DAY OF MONTH" &&
                    Array.from({ length: 30 }, (_, i) => `${i + 1}`).map((monthDay) => (
                      <option value={monthDay} key={monthDay}>
                        {monthDay}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {reminderName && dateType && dateValue && (
              <div className="row float-right">
                <button
                  className="btn btn-primary mr-1"
                  onClick={() => {
                    if (props.job) {
                      handleCreateReminder();
                    } else {
                      handleEditReminder();
                    }
                  }}
                  disabled={saveDisabled}
                >
                  {savingReminder && <InlineSpinner />}Save
                </button>
                <button className="btn btn-secondary" onClick={props.handleCloseReminderModal}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={!!errorMsg} onHide={() => setErrorMsg("")}>
        <Modal.Header closeButton>
          <Modal.Title>Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{errorMsg}</p>
        </Modal.Body>
      </Modal>
    </>
  );
}
